// PRISAI API Client
// SECURE VERSION - No auto-loading of credentials

import type {
  ApiResponse,
  ApiError,
  User,
  Parcel,
  Owner,
  Source,
  PropertyFeature,
  IngestionJob,
  SearchParams,
  AnalyticsSummary,
} from '@/types';

const API_BASE = 'https://prisai-api.cyopsys.workers.dev';

export class ApiClient {
  private apiKey: string | null = null;
  private sessionToken: string | null = null;

  // No auto-loading from storage - credentials must be explicitly set
  constructor() {
    // Credentials are NOT loaded from storage automatically
    // This prevents auto-login
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    } else if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers;
  }

  setApiKey(key: string): void {
    this.apiKey = key;
    // Also store for session persistence only
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('api_key_session', key);
    }
  }

  setSessionToken(token: string): void {
    this.sessionToken = token;
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('session_token', token);
    }
  }

  clearAuth(): void {
    this.sessionToken = null;
    this.apiKey = null;
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('session_token');
      sessionStorage.removeItem('api_key_session');
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'Unknown error',
        message: 'An unexpected error occurred',
        status: response.status,
      }));
      throw error;
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const data = await this.request<{ user: User; token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setSessionToken(data.token);
    return data;
  }

  async logout(): Promise<void> {
    await this.request('/api/auth/logout', { method: 'POST' });
    this.clearAuth();
  }

  async getCurrentUser(): Promise<User> {
    return this.request('/api/auth/me');
  }

  // Parcels
  async getParcels(params?: SearchParams): Promise<ApiResponse<Parcel[]>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request(`/api/parcels${query ? `?${query}` : ''}`);
  }

  async getParcel(id: string): Promise<Parcel> {
    return this.request(`/api/parcels/${id}`);
  }

  async getParcelFeatures(id: string): Promise<PropertyFeature[]> {
    return this.request(`/api/parcels/${id}/features`);
  }

  async enrichParcel(id: string): Promise<{ job_id: string }> {
    return this.request(`/api/parcels/${id}/features/enrich`, { method: 'POST' });
  }

  // Owners
  async getOwners(params?: SearchParams): Promise<ApiResponse<Owner[]>> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request(`/api/owners${query ? `?${query}` : ''}`);
  }

  async getOwner(id: string): Promise<Owner> {
    return this.request(`/api/owners/${id}`);
  }

  async getOwnerParcels(id: string): Promise<Parcel[]> {
    return this.request(`/api/owners/${id}/parcels`);
  }

  // Sources
  async getSources(): Promise<Source[]> {
    return this.request('/api/sources');
  }

  // Analytics
  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    return this.request('/api/analytics/summary');
  }

  // Admin
  async getJobs(): Promise<IngestionJob[]> {
    return this.request('/api/admin/jobs');
  }

  async triggerJob(): Promise<{ job_id: string }> {
    return this.request('/api/admin/jobs', { method: 'POST' });
  }
}

export const api = new ApiClient();
export default api;
